import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import type {
  IdealCustomerProfile,
  LeadSource,
  PersonSearchFilters,
} from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { getPeopleTechnologies } from "@/api/people";
import {
  EMAIL_STATUS_OPTIONS,
  EMPLOYEE_RANGES,
  SENIORITY_OPTIONS,
} from "./people-constants";
import {
  FILTER_SECTIONS,
  defaultOpenMap,
  type FilterSectionId,
} from "./people-filter-config";
import { RailIconButton } from "@/components/ui/rail-icon-button";
import {
  WORKSPACE_SOURCE_OPTIONS,
  dateInputToCreatedAfter,
  dateInputToCreatedBefore,
  isoToDateInput,
} from "@/lib/workspace-source";

export type ContactEmailStatus = NonNullable<
  PersonSearchFilters["contactEmailStatus"]
>[number];

export type PeopleFilterState = {
  personaIcpId?: string;
  personTitles: string[];
  includeSimilarTitles: boolean;
  personSeniorities: string[];
  personLocations: string[];
  organizationLocations: string[];
  qOrganizationName: string;
  organizationDomains: string[];
  organizationIds: string[];
  organizationNumEmployeesRanges: string[];
  organizationIndustryTagIds: string[];
  industries: string[];
  contactEmailStatus: ContactEmailStatus[];
  qKeywords: string;
  revenueMin?: number;
  revenueMax?: number;
  currentlyUsingAllOfTechnologyUids: string[];
  currentlyUsingAnyOfTechnologyUids: string[];
  currentlyNotUsingAnyOfTechnologyUids: string[];
  qOrganizationJobTitles: string[];
  organizationJobLocations: string[];
  organizationNumJobsMin?: number;
  organizationNumJobsMax?: number;
  organizationJobPostedAtMin: string;
  organizationJobPostedAtMax: string;
  /** Workspace Leads only */
  sources: LeadSource[];
  createdAfter: string;
  createdBefore: string;
};

export const EMPTY_PEOPLE_FILTERS: PeopleFilterState = {
  personaIcpId: undefined,
  personTitles: [],
  includeSimilarTitles: true,
  personSeniorities: [],
  personLocations: [],
  organizationLocations: [],
  qOrganizationName: "",
  organizationDomains: [],
  organizationIds: [],
  organizationNumEmployeesRanges: [],
  organizationIndustryTagIds: [],
  industries: [],
  contactEmailStatus: [],
  qKeywords: "",
  revenueMin: undefined,
  revenueMax: undefined,
  currentlyUsingAllOfTechnologyUids: [],
  currentlyUsingAnyOfTechnologyUids: [],
  currentlyNotUsingAnyOfTechnologyUids: [],
  qOrganizationJobTitles: [],
  organizationJobLocations: [],
  organizationNumJobsMin: undefined,
  organizationNumJobsMax: undefined,
  organizationJobPostedAtMin: "",
  organizationJobPostedAtMax: "",
  sources: [],
  createdAfter: "",
  createdBefore: "",
};

export function filtersToApi(
  f: PeopleFilterState,
  page: number,
  perPage: number,
): PersonSearchFilters {
  const industries = f.industries;
  const keywordParts = [f.qKeywords.trim(), ...industries].filter(Boolean);
  return {
    personTitles: f.personTitles,
    includeSimilarTitles: f.includeSimilarTitles,
    personSeniorities: f.personSeniorities,
    personLocations: f.personLocations,
    organizationLocations: f.organizationLocations,
    qOrganizationName: f.qOrganizationName.trim() || undefined,
    organizationDomains: f.organizationDomains,
    organizationIds: f.organizationIds,
    organizationNumEmployeesRanges: f.organizationNumEmployeesRanges,
    organizationIndustryTagIds: f.organizationIndustryTagIds,
    industries,
    contactEmailStatus: f.contactEmailStatus,
    qKeywords: keywordParts.length ? keywordParts.join(" ") : undefined,
    revenueMin: f.revenueMin,
    revenueMax: f.revenueMax,
    currentlyUsingAllOfTechnologyUids: f.currentlyUsingAllOfTechnologyUids,
    currentlyUsingAnyOfTechnologyUids: f.currentlyUsingAnyOfTechnologyUids,
    currentlyNotUsingAnyOfTechnologyUids:
      f.currentlyNotUsingAnyOfTechnologyUids,
    qOrganizationJobTitles: f.qOrganizationJobTitles,
    organizationJobLocations: f.organizationJobLocations,
    organizationNumJobsMin: f.organizationNumJobsMin,
    organizationNumJobsMax: f.organizationNumJobsMax,
    organizationJobPostedAtMin: f.organizationJobPostedAtMin || undefined,
    organizationJobPostedAtMax: f.organizationJobPostedAtMax || undefined,
    personaIcpId: f.personaIcpId,
    sources: f.sources.length ? f.sources : undefined,
    createdAfter: dateInputToCreatedAfter(f.createdAfter),
    createdBefore: dateInputToCreatedBefore(f.createdBefore),
    page,
    perPage,
  };
}

export function apiFiltersToState(
  f: PersonSearchFilters,
): PeopleFilterState {
  return {
    ...EMPTY_PEOPLE_FILTERS,
    personaIcpId: f.personaIcpId,
    personTitles: f.personTitles ?? [],
    includeSimilarTitles: f.includeSimilarTitles ?? true,
    personSeniorities: f.personSeniorities ?? [],
    personLocations: f.personLocations ?? [],
    organizationLocations: f.organizationLocations ?? [],
    qOrganizationName: f.qOrganizationName ?? "",
    organizationDomains: f.organizationDomains ?? [],
    organizationIds: f.organizationIds ?? [],
    organizationNumEmployeesRanges: f.organizationNumEmployeesRanges ?? [],
    organizationIndustryTagIds: f.organizationIndustryTagIds ?? [],
    industries: f.industries ?? [],
    contactEmailStatus: f.contactEmailStatus ?? [],
    qKeywords: f.qKeywords ?? "",
    revenueMin: f.revenueMin,
    revenueMax: f.revenueMax,
    currentlyUsingAllOfTechnologyUids:
      f.currentlyUsingAllOfTechnologyUids ?? [],
    currentlyUsingAnyOfTechnologyUids:
      f.currentlyUsingAnyOfTechnologyUids ?? [],
    currentlyNotUsingAnyOfTechnologyUids:
      f.currentlyNotUsingAnyOfTechnologyUids ?? [],
    qOrganizationJobTitles: f.qOrganizationJobTitles ?? [],
    organizationJobLocations: f.organizationJobLocations ?? [],
    organizationNumJobsMin: f.organizationNumJobsMin,
    organizationNumJobsMax: f.organizationNumJobsMax,
    organizationJobPostedAtMin: f.organizationJobPostedAtMin ?? "",
    organizationJobPostedAtMax: f.organizationJobPostedAtMax ?? "",
    sources: f.sources ?? [],
    createdAfter: isoToDateInput(f.createdAfter),
    createdBefore: isoToDateInput(f.createdBefore),
  };
}

/** Map ICP fields into people search filters (persona apply). */
export function seedFiltersFromIcp(
  icp: IdealCustomerProfile,
  prev: PeopleFilterState = EMPTY_PEOPLE_FILTERS,
): PeopleFilterState {
  const titleHints = (icp.requiredPersonas ?? []).map((r) =>
    String(r).replace(/_/g, " "),
  );
  return {
    ...prev,
    personaIcpId: icp.id,
    personTitles: titleHints.length ? titleHints : prev.personTitles,
    personLocations: icp.geographies?.length
      ? [...icp.geographies]
      : prev.personLocations,
    industries: icp.industries?.length ? [...icp.industries] : prev.industries,
    qKeywords: icp.icpKeywords?.length
      ? icp.icpKeywords.join(" ")
      : prev.qKeywords,
  };
}

function TagInput({
  label,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  function commit() {
    const v = draft.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft("");
  }
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-wrap gap-1 rounded-md bg-surface-hover p-1.5">
        {values.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            className="rounded bg-surface px-1.5 py-0.5 text-[10px] hover:bg-card"
          >
            {v} ×
          </button>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Backspace" && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={commit}
          placeholder={placeholder}
          className="min-w-[6rem] flex-1 bg-transparent px-1 py-0.5 text-xs outline-none"
        />
      </div>
    </label>
  );
}

function ChipMulti({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ id: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const active = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() =>
                onChange(
                  active
                    ? selected.filter((x) => x !== opt.id)
                    : [...selected, opt.id],
                )
              }
              className={
                active
                  ? "rounded bg-brand px-1.5 py-0.5 text-[10px] font-medium text-brand-foreground"
                  : "rounded bg-surface-hover px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterGroup({
  title,
  icon: Icon,
  open,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-xs font-semibold text-foreground hover:bg-surface-hover"
      >
        <Icon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate">{title}</span>
        <ChevronDown
          className={cn(
            "size-3.5 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && <div className="space-y-3.5 px-4 pb-4">{children}</div>}
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value?: number;
  onChange: (v: number | undefined) => void;
  placeholder?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? undefined : Number(raw));
        }}
        placeholder={placeholder}
        className="w-full rounded-md bg-surface-hover px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-border"
      />
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md bg-surface-hover px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-border"
      />
    </label>
  );
}

function TechTypeahead({
  label,
  values,
  onChange,
  technologies,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  technologies: string[];
}) {
  const [q, setQ] = useState("");
  const suggestions = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return technologies
      .filter((t) => t.toLowerCase().includes(needle) && !values.includes(t))
      .slice(0, 8);
  }, [q, technologies, values]);

  return (
    <div className="space-y-1">
      <TagInput label={label} values={values} onChange={onChange} placeholder="Type tech uid…" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search technologies…"
        className="w-full rounded-md bg-surface-hover px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-border"
      />
      {suggestions.length > 0 && (
        <ul className="max-h-28 overflow-y-auto rounded-md bg-popover text-xs shadow-lg ring-1 ring-black/5 dark:ring-white/10">
          {suggestions.map((t) => (
            <li key={t}>
              <button
                type="button"
                className="w-full px-2 py-1 text-left hover:bg-surface-hover"
                onClick={() => {
                  onChange([...values, t]);
                  setQ("");
                }}
              >
                {t}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PeopleFilters({
  value,
  onChange,
  onSearch,
  onClear,
  searching,
  collapsed,
  onToggleCollapsed,
  icps = [],
  activeIcpId,
  embedded = false,
  workspaceScope = false,
}: {
  value: PeopleFilterState;
  onChange: (next: PeopleFilterState) => void;
  onSearch: () => void;
  /** When set, Clear filters resets and re-runs search. */
  onClear?: () => void;
  searching?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  icps?: IdealCustomerProfile[];
  activeIcpId?: string;
  /** Render without sticky sidebar chrome (for mobile sheets). */
  embedded?: boolean;
  /** Show Leads-only filters (source, created date). */
  workspaceScope?: boolean;
}) {
  const [openMap, setOpenMap] = useState(defaultOpenMap);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { data: techData } = useQuery({
    queryKey: ["people-technologies"],
    queryFn: getPeopleTechnologies,
    staleTime: 300_000,
    retry: false,
    enabled: showAdvanced || openMap.technologies || openMap.excludeTech,
  });
  const technologies = techData?.technologies ?? [];

  const visibleSections = FILTER_SECTIONS.filter(
    (s) =>
      (showAdvanced || !s.advanced) &&
      (!s.workspaceOnly || workspaceScope),
  );

  function setOpen(id: FilterSectionId, open: boolean) {
    setOpenMap((m) => ({ ...m, [id]: open }));
  }

  function expandAll() {
    setShowAdvanced(true);
    setOpenMap(
      Object.fromEntries(FILTER_SECTIONS.map((s) => [s.id, true])) as Record<
        FilterSectionId,
        boolean
      >,
    );
  }

  function collapseAll() {
    setOpenMap(
      Object.fromEntries(FILTER_SECTIONS.map((s) => [s.id, false])) as Record<
        FilterSectionId,
        boolean
      >,
    );
  }

  if (collapsed && !embedded) {
    const collapsedSections = FILTER_SECTIONS.filter(
      (s) => !s.advanced && (!s.workspaceOnly || workspaceScope),
    );
    return (
      <aside className="sticky top-0 flex h-full w-12 shrink-0 flex-col items-center gap-0.5 overflow-y-auto rounded-xl border border-border bg-card py-3">
        <RailIconButton label="Show filters" onClick={onToggleCollapsed} className="mb-1">
          <PanelLeftOpen className="size-4" />
        </RailIconButton>
        <div className="mb-1 w-6 border-t border-border" />
        {collapsedSections.map((section) => {
          const Icon = section.icon;
          return (
            <RailIconButton
              key={section.id}
              label={section.label}
              onClick={() => {
                setOpen(section.id, true);
                onToggleCollapsed?.();
              }}
            >
              <Icon className="size-4" />
            </RailIconButton>
          );
        })}
      </aside>
    );
  }

  function renderSection(id: FilterSectionId) {
    switch (id) {
      case "personas":
        return (
          <div className="space-y-2">
            <p className="text-[11px] text-muted-foreground">
              Apply an ICP to seed titles, locations, industries, and keywords.
            </p>
            {icps.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                No ICPs yet. Create one under Settings → Personas.
              </p>
            ) : (
              <ul className="space-y-1">
                {icps.map((icp) => {
                  const active = value.personaIcpId === icp.id;
                  const isDefault = icp.id === activeIcpId;
                  return (
                    <li key={icp.id}>
                      <button
                        type="button"
                        onClick={() => onChange(seedFiltersFromIcp(icp, value))}
                        className={cn(
                          "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs",
                          active
                            ? "bg-brand/10 text-foreground ring-1 ring-brand/40"
                            : "hover:bg-surface-hover",
                        )}
                      >
                        <span className="truncate font-medium">{icp.name}</span>
                        {isDefault && (
                          <span className="text-[10px] text-muted-foreground">
                            Active
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      case "jobTitles":
        return (
          <>
            <TagInput
              label="Titles"
              values={value.personTitles}
              onChange={(personTitles) => onChange({ ...value, personTitles })}
              placeholder="CEO, VP Sales…"
            />
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={value.includeSimilarTitles}
                onChange={(e) =>
                  onChange({
                    ...value,
                    includeSimilarTitles: e.target.checked,
                  })
                }
              />
              Include similar titles
            </label>
          </>
        );
      case "seniority":
        return (
          <ChipMulti
            label="Seniority"
            options={SENIORITY_OPTIONS}
            selected={value.personSeniorities}
            onChange={(personSeniorities) =>
              onChange({ ...value, personSeniorities })
            }
          />
        );
      case "location":
        return (
          <TagInput
            label="Locations"
            values={value.personLocations}
            onChange={(personLocations) =>
              onChange({ ...value, personLocations })
            }
            placeholder="United States, London…"
          />
        );
      case "keywords":
        return (
          <TextField
            label="Keywords"
            value={value.qKeywords}
            onChange={(qKeywords) => onChange({ ...value, qKeywords })}
            placeholder="payments, AI"
          />
        );
      case "company":
        return (
          <TextField
            label="Company name"
            value={value.qOrganizationName}
            onChange={(qOrganizationName) =>
              onChange({ ...value, qOrganizationName })
            }
            placeholder="Acme Inc"
          />
        );
      case "emailStatus":
        return (
          <ChipMulti
            label="Email status"
            options={EMAIL_STATUS_OPTIONS}
            selected={value.contactEmailStatus}
            onChange={(contactEmailStatus) =>
              onChange({
                ...value,
                contactEmailStatus: contactEmailStatus as ContactEmailStatus[],
              })
            }
          />
        );
      case "source":
        return (
          <>
            <ChipMulti
              label="Source"
              options={WORKSPACE_SOURCE_OPTIONS}
              selected={value.sources}
              onChange={(sources) =>
                onChange({ ...value, sources: sources as LeadSource[] })
              }
            />
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="Created from"
                type="date"
                value={value.createdAfter}
                onChange={(createdAfter) =>
                  onChange({ ...value, createdAfter })
                }
              />
              <TextField
                label="Created to"
                type="date"
                value={value.createdBefore}
                onChange={(createdBefore) =>
                  onChange({ ...value, createdBefore })
                }
              />
            </div>
          </>
        );
      case "employees":
        return (
          <ChipMulti
            label="# Employees"
            options={EMPLOYEE_RANGES}
            selected={value.organizationNumEmployeesRanges}
            onChange={(organizationNumEmployeesRanges) =>
              onChange({ ...value, organizationNumEmployeesRanges })
            }
          />
        );
      case "industry":
        return (
          <>
            <TagInput
              label="Industries"
              values={value.industries}
              onChange={(industries) => onChange({ ...value, industries })}
              placeholder="fintech, SaaS"
            />
            <TagInput
              label="Industry tag IDs"
              values={value.organizationIndustryTagIds}
              onChange={(organizationIndustryTagIds) =>
                onChange({ ...value, organizationIndustryTagIds })
              }
              placeholder="Apollo tag ids"
            />
          </>
        );
      case "orgIds":
        return (
          <TagInput
            label="Organization IDs"
            values={value.organizationIds}
            onChange={(organizationIds) =>
              onChange({ ...value, organizationIds })
            }
            placeholder="Apollo org id"
          />
        );
      case "revenue":
        return (
          <div className="grid grid-cols-2 gap-2">
            <NumField
              label="Min"
              value={value.revenueMin}
              onChange={(revenueMin) => onChange({ ...value, revenueMin })}
              placeholder="1000000"
            />
            <NumField
              label="Max"
              value={value.revenueMax}
              onChange={(revenueMax) => onChange({ ...value, revenueMax })}
              placeholder="50000000"
            />
          </div>
        );
      case "technologies":
        return (
          <>
            <TechTypeahead
              label="Using any of"
              values={value.currentlyUsingAnyOfTechnologyUids}
              onChange={(currentlyUsingAnyOfTechnologyUids) =>
                onChange({ ...value, currentlyUsingAnyOfTechnologyUids })
              }
              technologies={technologies}
            />
            <TechTypeahead
              label="Using all of"
              values={value.currentlyUsingAllOfTechnologyUids}
              onChange={(currentlyUsingAllOfTechnologyUids) =>
                onChange({ ...value, currentlyUsingAllOfTechnologyUids })
              }
              technologies={technologies}
            />
          </>
        );
      case "excludeTech":
        return (
          <TechTypeahead
            label="Not using any of"
            values={value.currentlyNotUsingAnyOfTechnologyUids}
            onChange={(currentlyNotUsingAnyOfTechnologyUids) =>
              onChange({ ...value, currentlyNotUsingAnyOfTechnologyUids })
            }
            technologies={technologies}
          />
        );
      case "hiring":
        return (
          <>
            <TagInput
              label="Job titles at org"
              values={value.qOrganizationJobTitles}
              onChange={(qOrganizationJobTitles) =>
                onChange({ ...value, qOrganizationJobTitles })
              }
              placeholder="engineer, salesperson"
            />
            <TagInput
              label="Job locations"
              values={value.organizationJobLocations}
              onChange={(organizationJobLocations) =>
                onChange({ ...value, organizationJobLocations })
              }
              placeholder="Remote, NYC"
            />
            <div className="grid grid-cols-2 gap-2">
              <NumField
                label="Jobs min"
                value={value.organizationNumJobsMin}
                onChange={(organizationNumJobsMin) =>
                  onChange({ ...value, organizationNumJobsMin })
                }
              />
              <NumField
                label="Jobs max"
                value={value.organizationNumJobsMax}
                onChange={(organizationNumJobsMax) =>
                  onChange({ ...value, organizationNumJobsMax })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="Posted from"
                type="date"
                value={value.organizationJobPostedAtMin}
                onChange={(organizationJobPostedAtMin) =>
                  onChange({ ...value, organizationJobPostedAtMin })
                }
              />
              <TextField
                label="Posted to"
                type="date"
                value={value.organizationJobPostedAtMax}
                onChange={(organizationJobPostedAtMax) =>
                  onChange({ ...value, organizationJobPostedAtMax })
                }
              />
            </div>
          </>
        );
      case "orgDomains":
        return (
          <>
            <TagInput
              label="HQ locations"
              values={value.organizationLocations}
              onChange={(organizationLocations) =>
                onChange({ ...value, organizationLocations })
              }
              placeholder="California, United States"
            />
            <TagInput
              label="Domains"
              values={value.organizationDomains}
              onChange={(organizationDomains) =>
                onChange({ ...value, organizationDomains })
              }
              placeholder="acme.com"
            />
          </>
        );
      default:
        return null;
    }
  }

  return (
    <aside
      className={cn(
        "flex flex-col overflow-hidden bg-card",
        embedded
          ? "h-full w-full border-0"
          : "sticky top-0 h-full w-72 shrink-0 rounded-xl border border-border",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-border px-4 py-3.5">
        {!embedded && (
          <h2 className="mr-auto text-sm font-semibold text-foreground">
            Filters
          </h2>
        )}
        {embedded && <div className="mr-auto" />}
        <button
          type="button"
          onClick={expandAll}
          className="text-[11px] text-brand hover:underline"
        >
          Expand
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Collapse
        </button>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          {showAdvanced ? "Fewer" : "More"}
        </button>
        {onToggleCollapsed && !embedded && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            title="Hide filters"
            className="rounded-md p-1 text-muted-foreground hover:bg-surface-hover hover:text-foreground"
          >
            <PanelLeftClose className="size-3.5" />
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {visibleSections.map((section) => (
          <FilterGroup
            key={section.id}
            title={section.label}
            icon={section.icon}
            open={!!openMap[section.id]}
            onToggle={() => setOpen(section.id, !openMap[section.id])}
          >
            {renderSection(section.id)}
          </FilterGroup>
        ))}
      </div>

      <div className="space-y-2.5 border-t border-border p-4">
        <button
          type="button"
          onClick={onSearch}
          disabled={searching}
          className="w-full rounded-md bg-brand px-3 py-2.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
        >
          {searching ? "Searching…" : "Apply filters"}
        </button>
        <button
          type="button"
          onClick={() =>
            onClear ? onClear() : onChange(EMPTY_PEOPLE_FILTERS)
          }
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
        >
          Clear filters
        </button>
      </div>
    </aside>
  );
}
